import { EWS_API_BASE_URL } from '@/core/constants/appConfig/environment';
import { callAPI } from '@/shared/fetch_client';
import { Logger } from '@/shared/logger/logger';
import { getEwsWorker } from '@/workers/init_workers';

import { EDataLayerType } from './datalayer_listener';

/**
 * Interface for the data layer event payload
 */
export type IDataLayerEventPayload = {
  [key: string]: any;
  event: string;
  event_id?: string;
};

/**
 * Utility class for sending data layer events to the EWS API endpoint
 */
export class DataLayerEventSender {
  /**
   * Send a data layer event to the EWS API endpoint via the worker
   * @param layerType The type of data layer
   * @param eventPayload The event payload from the data layer
   * @param debug Enable debug logging
   * @returns Promise that resolves when the event is sent
   */
  public static async sendEvent(
    layerType: EDataLayerType,
    eventPayload: IDataLayerEventPayload,
  ): Promise<void> {
    // Create a non-blocking promise that will be resolved later
    return new Promise<void>(resolve => {
      // Use setTimeout with 0ms delay to move execution to the end of the event loop
      // This prevents blocking the main thread
      setTimeout(async () => {
        try {
          const workerManager = getEwsWorker();
          Logger.debug('DataLayerEventSender: Sending event to EWS worker', {
            eventPayload,
            layerType,
          });

          // If worker is available, use it to send the event
          if (workerManager?.isInitialized()) {
            Logger.debug('Using worker to send data layer event');

            // Create a trace ID based on event name or ID if available
            const traceId = `dl-${eventPayload.event || 'event'}-${eventPayload.event_id || Date.now()}`;

            // Use Logger.profileFnAsync instead of manual profiling
            await Logger.profileFnAsync(
              'submitTask:SEND_DATA_LAYER_EVENT',
              () =>
                workerManager.submitTask(
                  'SEND_DATA_LAYER_EVENT',
                  {
                    eventPayload,
                    layerType,
                  },
                  error => {
                    if (error) {
                      Logger.error(
                        'Error in background SEND_DATA_LAYER_EVENT task',
                        error,
                      );
                    } else {
                      Logger.debug(
                        'Background SEND_DATA_LAYER_EVENT task completed',
                      );
                    }
                  },
                ),
              traceId,
            );

            resolve();
          } else {
            Logger.debug(
              'Worker not available, using direct API call for data layer event',
            );

            const traceId = `dl-direct-${eventPayload.event || 'event'}-${eventPayload.event_id || Date.now()}`;

            const eventId = eventPayload.event_id || '';

            const params: Record<string, string> = {
              event_id: eventId,
              event_name: eventPayload.event,
              input_event_id: layerType,
              source_type: 'data_layer',
              source_uid: eventPayload['source_uid'] || 'unknown',
            };

            // Remove undefined values from params
            const cleanParams = Object.fromEntries(
              Object.entries(params).filter(([, value]) => value !== undefined),
            );

            // Prepare the request body (payload)
            const bodyPayload = {
              ...eventPayload,
              data_layer_type: layerType,
              timestamp: new Date().toISOString(),
            };

            await Logger.profileFnAsync(
              'directAPI:SEND_DATA_LAYER_EVENT',
              async () =>
                callAPI<{ ok: boolean; status: number }>({
                  body: JSON.stringify(bodyPayload),
                  method: 'POST',
                  params: cleanParams,
                  url: `${EWS_API_BASE_URL}/ews/generic-pixel`,
                }),
              traceId,
            );

            resolve();
          }
        } catch (error) {
          Logger.error('DataLayerEventSender: Failed to send event', {
            error,
            eventPayload,
            layerType,
          });
          resolve();
        }
      }, 0);
    });
  }

  /**
   * Send multiple events in batch
   * @param events Array of events to send
   * @param debug Enable debug logging
   * @returns Promise that resolves when all events are sent
   */
  public static async sendEvents(
    events: {
      eventPayload: IDataLayerEventPayload;
      layerType: EDataLayerType;
    }[],
  ): Promise<void> {
    const promises = events.map(({ eventPayload, layerType }) =>
      DataLayerEventSender.sendEvent(layerType, eventPayload),
    );
    await Promise.allSettled(promises);
  }
}
